import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Package, Truck, AlertCircle, Loader2 } from "lucide-react";
import { formatINR } from "@/data/products";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const mid = params.get("mid");
  const [status, setStatus] = useState("loading"); // loading | success | failed | pending | error
  const [order, setOrder] = useState(null);
  const [innayaa, setInnayaa] = useState(null);

  useEffect(() => {
    if (!mid) {
      setStatus("error");
      return;
    }
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/payments/phonepe/${encodeURIComponent(mid)}/status`);
        const data = await r.json();
        setOrder(data.order || null);
        setInnayaa(data.innayaa || null);
        const s = (data.state || "").toUpperCase();
        if (s === "COMPLETED") setStatus("success");
        else if (s === "FAILED") setStatus("failed");
        else setStatus("pending");
      } catch (e) {
        setStatus("error");
      }
    })();
  }, [mid]);

  return (
    <div data-testid="payment-return-page" className="bg-[#FAF8F5]">
      <section className="max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 pt-14 md:pt-20 pb-12 md:pb-20 text-center">
        {status === "loading" && (
          <div data-testid="payment-loading" className="flex flex-col items-center py-16">
            <Loader2 className="animate-spin text-[#2A2626]" size={40} strokeWidth={1.5} />
            <p className="mt-6 overline">Verifying your payment</p>
            <p className="mt-3 text-[#5C564F]">One moment please — confirming with PhonePe.</p>
          </div>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#2A2626] text-[#FAF8F5] flex items-center justify-center">
              <Check strokeWidth={1.5} size={26} />
            </div>
            <p className="overline mt-6" data-testid="payment-success">Payment successful</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#2A2626] leading-tight">
              Thank you{innayaa?.customer?.fullName ? `, ${innayaa.customer.fullName.split(" ")[0]}` : ""}.
            </h1>
            <p className="mt-5 text-[15px] md:text-base text-[#5C564F] max-w-lg mx-auto leading-relaxed">
              Your Innayaa is being lovingly prepared. A confirmation email is on its way,
              and we&rsquo;ll be in touch when your parcel is dispatched.
            </p>
            {innayaa?.id && (
              <div className="mt-8 inline-flex items-center gap-3 bg-[#F0EBE1] px-5 py-3">
                <span className="overline">Order ID</span>
                <span data-testid="return-order-id" className="font-serif text-lg text-[#2A2626]">
                  {innayaa.id}
                </span>
              </div>
            )}
            {innayaa?.totals?.total != null && (
              <p className="mt-4 text-sm text-[#5C564F]">
                Paid: <span className="text-[#2A2626]">{formatINR(innayaa.totals.total)}</span> via PhonePe
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 mt-10 max-w-[720px] mx-auto">
              {[
                { Icon: Truck, t: "Ships in 2–3 working days" },
                { Icon: Package, t: "In our keepsake box" },
                { Icon: Check, t: "Payment received" },
              ].map(({ Icon, t }) => (
                <div key={t} className="bg-[#F0EBE1]/60 p-4 md:p-5 flex items-center gap-3 text-left">
                  <Icon size={18} strokeWidth={1.25} className="text-[#2A2626] shrink-0" />
                  <span className="text-sm text-[#2A2626]">{t}</span>
                </div>
              ))}
            </div>
            <Link
              to="/shop"
              data-testid="return-continue-shopping"
              className="mt-10 inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
            >
              Continue shopping
            </Link>
          </>
        )}

        {(status === "failed" || status === "error") && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full border-2 border-[#2A2626] text-[#2A2626] flex items-center justify-center">
              <AlertCircle strokeWidth={1.5} size={26} />
            </div>
            <p className="overline mt-6" data-testid="payment-failed">Payment {status === "failed" ? "was not completed" : "verification failed"}</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-[#2A2626] leading-tight">
              We couldn&rsquo;t confirm your payment.
            </h1>
            <p className="mt-5 text-[15px] text-[#5C564F] max-w-lg mx-auto leading-relaxed">
              No amount has been captured. Please try again, or contact us at
              innayaaofficial@gmail.com if you need help. If money was debited from your
              account, it will be auto-refunded within 3–5 business days by your bank / UPI
              provider.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/checkout" className="inline-block bg-[#2A2626] text-[#FAF8F5] px-9 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors">
                Try payment again
              </Link>
              <Link to="/cart" className="text-[11px] uppercase tracking-[0.22em] text-[#2A2626] link-underline pb-1">
                Back to bag
              </Link>
            </div>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full border-2 border-[#2A2626] text-[#2A2626] flex items-center justify-center">
              <Loader2 className="animate-spin" strokeWidth={1.5} size={26} />
            </div>
            <p className="overline mt-6" data-testid="payment-pending">Payment pending</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-[#2A2626] leading-tight">
              Your payment is being confirmed.
            </h1>
            <p className="mt-5 text-[15px] text-[#5C564F] max-w-lg mx-auto leading-relaxed">
              PhonePe hasn&rsquo;t sent us the final confirmation yet. Please refresh this
              page in a moment, or check your email for the order confirmation.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-8 inline-block bg-[#2A2626] text-[#FAF8F5] px-9 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
            >
              Refresh
            </button>
          </>
        )}
      </section>
    </div>
  );
}
